import { useEffect, useMemo, useRef, useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Search, Printer, User, Trash2, FileSpreadsheet } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { PrintableReport } from "./PrintableReport";
import { exportToCsv } from "@/lib/exportCsv";

interface Student {
  id: string;
  first_name: string;
  last_name: string;
  student_number: string;
  class: string | null;
  program: string | null;
}

interface LogRow {
  id: string;
  date: string;
  detail: string;
  status: string;
  time_in: string;
  time_out: string | null;
}

export function StudentAttendanceReport() {
  const { hasRole } = useAuth();
  const canDelete = hasRole("superadmin");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<Student[]>([]);
  const [picked, setPicked] = useState<Student | null>(null);
  const [mode, setMode] = useState<"gate" | "session">("gate");
  const [from, setFrom] = useState<Date | undefined>();
  const [to, setTo] = useState<Date | undefined>();
  const [logs, setLogs] = useState<LogRow[]>([]);
  const printRef = useRef<HTMLDivElement>(null);

  // Debounced student search
  useEffect(() => {
    if (picked) return;
    const s = search.trim();
    if (s.length < 2) { setResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from("students")
        .select("id, first_name, last_name, student_number, class, program")
        .or(`first_name.ilike.%${s}%,last_name.ilike.%${s}%,student_number.ilike.%${s}%`)
        .limit(10);
      setResults((data ?? []) as Student[]);
    }, 300);
    return () => clearTimeout(t);
  }, [search, picked]);

  // Load logs when student + mode set
  useEffect(() => {
    if (!picked) { setLogs([]); return; }
    (async () => {
      let q = supabase
        .from("attendance_records")
        .select("id, attendance_date, attendance_type, scanned_at, time_out, status, attendance_sessions(module_code, topic)")
        .eq("student_id", picked.id);
      if (mode === "gate") {
        q = q.in("attendance_type", ["general_entry", "general_exit", "gate"]);
      } else {
        q = q.eq("attendance_type", "session");
      }
      q = q.order("scanned_at", { ascending: true }).limit(1000);
      if (from) q = q.gte("attendance_date", format(from, "yyyy-MM-dd"));
      if (to) q = q.lte("attendance_date", format(to, "yyyy-MM-dd"));
      const { data, error } = await q;
      if (error) {
        console.error("Failed to load student attendance:", error);
        toast.error("Failed to load attendance: " + error.message);
        setLogs([]);
        return;
      }

      if (mode === "gate") {
        const inEntryWindow = (iso: string) => {
          const d = new Date(iso);
          const mins = d.getHours() * 60 + d.getMinutes();
          return mins >= 6 * 60 && mins <= 8 * 60 + 30;
        };
        const inExitWindow = (iso: string) => {
          const d = new Date(iso);
          const mins = d.getHours() * 60 + d.getMinutes();
          return mins >= 15 * 60 + 30 && mins <= 16 * 60 + 30;
        };

        // Pair entry/exit per day (strict by type, numeric comparison, time-window filtered)
        const grouped = new Map<string, LogRow>();
        for (const r of (data ?? []) as any[]) {
          const key = r.attendance_date;
          let existing = grouped.get(key);
          const isExit = r.attendance_type === "general_exit";
          const isEntry = r.attendance_type === "general_entry" || r.attendance_type === "gate";
          if (!existing) {
            existing = {
              id: r.id,
              date: r.attendance_date,
              detail: "Main Gate",
              status: r.status ?? "present",
              time_in: "",
              time_out: null,
            };
            grouped.set(key, existing);
          }
          const t = Date.parse(r.scanned_at);
          if (isEntry && inEntryWindow(r.scanned_at)) {
            if (!existing.time_in || t < Date.parse(existing.time_in)) existing.time_in = r.scanned_at;
          }
          if (isExit && inExitWindow(r.scanned_at)) {
            if (!existing.time_out || t > Date.parse(existing.time_out)) existing.time_out = r.scanned_at;
          }
        }
        setLogs(Array.from(grouped.values()).sort((a, b) => b.date.localeCompare(a.date)));
      } else {
        const mapped: LogRow[] = (data ?? []).map((r: any) => ({
          id: r.id,
          date: r.attendance_date,
          detail: `${r.attendance_sessions?.module_code ?? "—"} ${r.attendance_sessions?.topic ? "— " + r.attendance_sessions.topic : ""}`.trim(),
          status: r.status ?? "present",
          time_in: r.scanned_at,
          time_out: r.time_out,
        }));
        setLogs(mapped.sort((a, b) => b.date.localeCompare(a.date)));
      }
    })();
  }, [picked, mode, from, to]);

  const stats = useMemo(() => {
    const total = logs.length;
    const present = logs.filter((l) => l.status === "present").length;
    const pct = total > 0 ? Math.round((present / total) * 100) : 0;
    return { total, present, pct, eligible: pct >= 80 };
  }, [logs]);

  async function handleDelete(row: LogRow) {
    if (!picked) return;
    let q = supabase.from("attendance_records").delete().eq("student_id", picked.id).eq("attendance_date", row.date);
    if (mode === "gate") {
      q = q.in("attendance_type", ["general_entry", "general_exit", "gate"]);
    } else {
      q = q.eq("attendance_type", "session");
    }
    const { error } = await q;
    if (error) {
      toast.error("Failed to delete record: " + error.message);
      return;
    }
    setLogs((prev) => prev.filter((l) => l.date !== row.date));
    toast.success("Attendance record deleted");
  }

  const filterSummary = picked
    ? `${picked.first_name} ${picked.last_name} (${picked.student_number}) • ${mode === "gate" ? "Main Gate" : "Session"}${from ? " • From " + format(from, "PP") : ""}${to ? " • To " + format(to, "PP") : ""}`
    : "";

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6 space-y-3">
          <Label className="text-xs">Search Student (name or registration number)</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Type at least 2 characters…"
              value={picked ? `${picked.first_name} ${picked.last_name} — ${picked.student_number}` : search}
              onChange={(e) => { setPicked(null); setSearch(e.target.value); }}
            />
          </div>
          {!picked && results.length > 0 && (
            <div className="border rounded-md divide-y max-h-60 overflow-auto">
              {results.map((s) => (
                <button
                  key={s.id}
                  className="w-full text-left p-2 hover:bg-muted flex justify-between items-center"
                  onClick={() => { setPicked(s); setResults([]); setSearch(""); }}
                >
                  <span className="font-medium">{s.first_name} {s.last_name}</span>
                  <span className="font-mono text-xs text-muted-foreground">{s.student_number} • {s.class ?? "—"}</span>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {picked && (
        <>
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10"><User className="w-5 h-5 text-primary" /></div>
                <div className="flex-1">
                  <p className="font-semibold">{picked.first_name} {picked.last_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">{picked.student_number} • {picked.class ?? "—"} • {picked.program ?? "—"}</p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setPicked(null)}>Change</Button>
              </div>

              <Tabs value={mode} onValueChange={(v) => setMode(v as any)}>
                <TabsList className="grid grid-cols-2 w-full md:w-80">
                  <TabsTrigger value="gate">Main Gate</TabsTrigger>
                  <TabsTrigger value="session">Sessions</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !from && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />{from ? format(from, "PP") : "Any"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={from} onSelect={setFrom} className={cn("p-3 pointer-events-auto")} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !to && "text-muted-foreground")}>
                        <CalendarIcon className="mr-2 h-4 w-4" />{to ? format(to, "PP") : "Any"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar mode="single" selected={to} onSelect={setTo} className={cn("p-3 pointer-events-auto")} />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex items-end gap-2">
                  <Button
                    variant="outline"
                    className="flex-1"
                    disabled={logs.length === 0}
                    onClick={() => {
                      const headers = ["SN", "Date", mode === "gate" ? "Location" : "Module / Topic", "Time In", "Time Out", "Status"];
                      const rows = logs.map((r, i) => [
                        String(i + 1),
                        format(new Date(r.date), "yyyy-MM-dd"),
                        r.detail,
                        r.time_in ? format(new Date(r.time_in), "HH:mm") : "",
                        r.time_out ? format(new Date(r.time_out), "HH:mm") : "",
                        r.status,
                      ]);
                      const stamp = format(new Date(), "yyyyMMdd-HHmm");
                      const safe = `${picked!.student_number}-${mode}`.replace(/[^a-zA-Z0-9_-]/g, "_");
                      exportToCsv(`student-attendance-${safe}-${stamp}.csv`, headers, rows);
                    }}
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" /> CSV
                  </Button>
                  <Button className="flex-1" onClick={() => window.print()} disabled={logs.length === 0}>
                    <Printer className="w-4 h-4 mr-1" /> PDF
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Total Records</p></CardContent></Card>
                <Card><CardContent className="py-3 text-center"><p className="text-2xl font-bold">{stats.pct}%</p><p className="text-xs text-muted-foreground">Present Rate</p></CardContent></Card>
                <Card><CardContent className="py-3 text-center">
                  <Badge variant={stats.eligible ? "default" : "destructive"} className="text-sm">{stats.eligible ? "Eligible" : "Not Eligible"}</Badge>
                  <p className="text-xs text-muted-foreground mt-1">≥80% threshold</p>
                </CardContent></Card>
              </div>
            </CardContent>
          </Card>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">SN</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>{mode === "gate" ? "Location" : "Module / Topic"}</TableHead>
                  <TableHead>Time In</TableHead>
                  <TableHead>Time Out</TableHead>
                  <TableHead>Status</TableHead>
                  {canDelete && <TableHead className="w-12 text-right print:hidden">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow><TableCell colSpan={canDelete ? 7 : 6} className="text-center text-muted-foreground py-8">No records.</TableCell></TableRow>
                ) : logs.map((r, i) => (
                  <TableRow key={r.id}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{format(new Date(r.date), "PP")}</TableCell>
                    <TableCell>{r.detail}</TableCell>
                    <TableCell>{r.time_in ? format(new Date(r.time_in), "hh:mm a") : "—"}</TableCell>
                    <TableCell>{r.time_out ? format(new Date(r.time_out), "hh:mm a") : "—"}</TableCell>
                    <TableCell><Badge variant={r.status === "present" ? "default" : "secondary"}>{r.status}</Badge></TableCell>
                    {canDelete && (
                      <TableCell className="text-right print:hidden">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete attendance record?</AlertDialogTitle>
                              <AlertDialogDescription>
                                This will permanently delete the {mode === "gate" ? "gate scans" : "session record"} for {format(new Date(r.date), "PP")}. This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(r)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <PrintableReport ref={printRef} title="Student Attendance Report" filterSummary={filterSummary}>
            <p style={{ marginBottom: 8 }}>
              <strong>Total:</strong> {stats.total} • <strong>Present:</strong> {stats.present} • <strong>Rate:</strong> {stats.pct}% • <strong>Eligibility:</strong> {stats.eligible ? "Eligible" : "Not Eligible"}
            </p>
            <table>
              <thead>
                <tr><th>SN</th><th>Date</th><th>{mode === "gate" ? "Location" : "Module"}</th><th>Time In</th><th>Time Out</th><th>Status</th></tr>
              </thead>
              <tbody>
                {logs.map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td>
                    <td>{format(new Date(r.date), "PP")}</td>
                    <td>{r.detail}</td>
                    <td>{r.time_in ? format(new Date(r.time_in), "hh:mm a") : "—"}</td>
                    <td>{r.time_out ? format(new Date(r.time_out), "hh:mm a") : "—"}</td>
                    <td>{r.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </PrintableReport>
        </>
      )}
    </div>
  );
}
